import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Paper, Snackbar, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import RefreshIcon      from '@mui/icons-material/Refresh';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import CancelIcon       from '@mui/icons-material/Cancel';
import AccessTimeIcon   from '@mui/icons-material/AccessTime';
import { getPendingBreaks, approveBreak, rejectBreak, isAdmin, isSupervisor } from '../api/client';
import useTimezone from '../hooks/useTimezone';
import { formatTime } from '../utils/format';

const REASON_LABEL = {
  coffee: '☕ Coffee', lunch: '🍱 Lunch', personal: '🚶 Personal',
  restroom: '🚻 Restroom', medical: '🏥 Medical', other: '⏸ Other',
};

function ApproveDialog({ open, onClose, breakItem, onDone }) {
  const [passkey, setPasskey] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  useEffect(() => { if (open) { setPasskey(''); setError(''); } }, [open]);
  const handle = async () => {
    setSaving(true); setError('');
    try {
      const result = await approveBreak(breakItem.id, passkey || undefined);
      const startsIn = result.starts_in_seconds ? Math.ceil(result.starts_in_seconds / 60) : 1;
      onDone(`Break approved. Starts in ~${startsIn} min.`);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Approval failed.');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleIcon color="success" /> Approve Break
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f9f5', borderLeft: '4px solid #4caf50' }}>
          <Typography variant="body2" fontWeight={700}>
            {breakItem?.operator_name}
            <Typography component="span" color="text.secondary" fontWeight={400} ml={0.5}>
              #{breakItem?.operator_employee_id}
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {REASON_LABEL[breakItem?.reason] || breakItem?.reason}
          </Typography>
        </Paper>
        <Alert severity="info" icon={<AccessTimeIcon />} sx={{ mb: 2, py: 0.5 }}>
          Break starts <strong>1 minute</strong> after approval.
        </Alert>
        {isSupervisor() && (
          <TextField label="Your supervisor PIN" type="password"
            inputProps={{ maxLength: 8, inputMode: 'numeric' }}
            value={passkey} onChange={e => setPasskey(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => e.key === 'Enter' && handle()}
            fullWidth size="small" autoFocus />
        )}
        {isAdmin() && (
          <Alert severity="success" sx={{ py: 0.5 }}>Admin approval — no PIN required.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handle} variant="contained" color="success" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RejectDialog({ open, onClose, breakItem, onDone }) {
  const [passkey, setPasskey] = useState('');
  const [reason,  setReason]  = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  useEffect(() => { if (open) { setPasskey(''); setReason(''); setError(''); } }, [open]);
  const handle = async () => {
    setSaving(true); setError('');
    try {
      await rejectBreak(breakItem.id, passkey || undefined, reason || undefined);
      onDone(`Break rejected for ${breakItem.operator_name}.`);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Rejection failed.');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CancelIcon color="error" /> Reject Break
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" mb={2}>
          Reject break for <strong>{breakItem?.operator_name}</strong>?
        </Typography>
        {isSupervisor() && (
          <TextField label="Your supervisor PIN" type="password"
            inputProps={{ maxLength: 8, inputMode: 'numeric' }}
            value={passkey} onChange={e => setPasskey(e.target.value.replace(/\D/g, ''))}
            fullWidth size="small" sx={{ mb: 2 }} autoFocus />
        )}
        <TextField label="Reason (shown to operator)"
          value={reason} onChange={e => setReason(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handle()}
          fullWidth size="small" multiline rows={2} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handle} variant="contained" color="error" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BreaksPage() {
  const tz = useTimezone();
  const [breaks,        setBreaks]        = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget,  setRejectTarget]  = useState(null);
  const [toast,         setToast]         = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await getPendingBreaks();
      setBreaks(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load pending breaks.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, isSupervisor() ? 10_000 : 20_000);
    return () => clearInterval(t);
  }, [load]);

  const handleDone = (msg) => { setToast(msg); load(); };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pending Breaks</Typography>
          <Typography variant="body2" color="text.secondary">
            {breaks.length} request{breaks.length !== 1 ? 's' : ''} awaiting approval
            {isSupervisor() && ' · popup notification on new requests (any page)'}
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {isAdmin() && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          Admin approval does not require a PIN.
        </Alert>
      )}

      {loading && breaks.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Operator</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Requested At</TableCell>
                <TableCell>Waiting</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {breaks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No pending break requests.
                  </TableCell>
                </TableRow>
              ) : breaks.map(brk => {
                const req = brk.requested_at ? new Date(brk.requested_at) : null;
                const waitMin = req ? Math.floor((Date.now() - req.getTime()) / 60_000) : null;
                return (
                  <TableRow key={brk.id} hover sx={{ bgcolor: waitMin >= 10 ? '#fff8f0' : 'inherit' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{brk.operator_name || brk.operator_id}</Typography>
                      {brk.operator_employee_id && (
                        <Typography variant="caption" color="text.secondary">#{brk.operator_employee_id}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={REASON_LABEL[brk.reason] || brk.reason} size="small"
                        color={brk.reason === 'medical' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                        {formatTime(brk.requested_at, tz)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={waitMin >= 5 ? 600 : 400}
                        color={waitMin >= 10 ? 'error.main' : waitMin >= 5 ? 'warning.main' : 'text.primary'}>
                        {waitMin !== null ? `${waitMin} min` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Approve (starts in 1 min)">
                        <IconButton size="small" color="success" onClick={() => setApproveTarget(brk)}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton size="small" color="error" onClick={() => setRejectTarget(brk)}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <ApproveDialog open={!!approveTarget} breakItem={approveTarget}
        onClose={() => setApproveTarget(null)} onDone={handleDone} />
      <RejectDialog open={!!rejectTarget} breakItem={rejectTarget}
        onClose={() => setRejectTarget(null)} onDone={handleDone} />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}